import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { ShortContentItem } from '@/hooks/useShortContent';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Share2, Calendar, User, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet';
import { Badge } from '@/components/ui/badge';
import { RichTextViewer } from '@/components/ui/rich-text-viewer/RichTextViewer';
import { Skeleton } from '@/components/ui/skeleton';
import { TravelByteStructuredData } from '@/components/structured-data/TravelByteStructuredData';
import { TravelByteOpenGraph } from '@/components/structured-data/TravelByteOpenGraph';
import { TravelByteBreadcrumbs } from '@/components/travel-bytes/TravelByteBreadcrumbs';

export default function ShortContentDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<ShortContentItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const baseUrl = window.location.origin;
  
  useEffect(() => {
    const fetchContentBySlug = async () => {
      if (!slug) {
        setError("Content not found");
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      console.log('Fetching content for slug:', slug);
      
      try {
        const { data, error: fetchError } = await supabase
          .from('short_content')
          .select(`
            id,
            title,
            content,
            content_type,
            image_url,
            category,
            source,
            status,
            is_featured,
            publish_date,
            view_count,
            created_at,
            updated_at,
            author_id,
            display_order,
            slug
          `)
          .eq('slug', slug)
          .eq('status', 'published')
          .single();
        
        if (fetchError) {
          console.error('Error fetching content:', fetchError);
          setError("Content not found");
          return;
        }
        
        if (!data) {
          console.log('No content found for slug:', slug);
          setError("Content not found");
          return;
        }
        
        console.log('Content found:', data);
        setItem(data as ShortContentItem);
        
        // Increment view count
        try {
          await supabase.rpc('increment_short_content_view_count', { content_id: data.id });
          // Update local state to reflect the incremented view count
          setItem(prev => prev ? { ...prev, view_count: prev.view_count + 1 } : prev);
        } catch (viewError) {
          console.error('Error incrementing view count:', viewError);
          // Don't set error state for view count failures
        }
        
      } catch (err) {
        console.error('Unexpected error:', err);
        setError("Failed to load content");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchContentBySlug();
  }, [slug]);
  
  const handleBack = () => {
    navigate('/travel-bytes');
  };
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: item?.title || 'Travel Byte',
          text: `Check out this travel byte: ${item?.title}`,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback to clipboard copy
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  
  if (isLoading) {
    return (
      <div className="container max-w-4xl py-12">
        <TravelByteBreadcrumbs baseUrl={baseUrl} />
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-8" />
        <Skeleton className="h-48 mb-6" />
        <Skeleton className="h-6 w-full mb-4" />
        <Skeleton className="h-6 w-full mb-4" />
        <Skeleton className="h-6 w-3/4" />
      </div>
    );
  }
  
  if (error || !item) {
    return (
      <div className="container py-12">
        <TravelByteBreadcrumbs baseUrl={baseUrl} />
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Content Not Found</h1>
          <p className="mb-6 text-muted-foreground">The travel byte you're looking for doesn't exist or has been removed.</p>
          <Button onClick={handleBack}>Back to Travel Bytes</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container max-w-4xl py-12">
      {/* SEO Components */}
      <TravelByteOpenGraph item={item} baseUrl={baseUrl} />
      <TravelByteStructuredData item={item} baseUrl={baseUrl} />
      
      {/* Breadcrumbs */}
      <TravelByteBreadcrumbs item={item} baseUrl={baseUrl} />
      
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Travel Bytes
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-3xl font-bold mb-2 md:mb-0">{item.title}</h1>
          <Button variant="outline" onClick={handleShare} className="w-fit">
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-6">
          <Badge variant="secondary" className="flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {item.content_type}
          </Badge>
          
          {item.category && (
            <Badge variant="outline" className="flex items-center gap-1">
              {item.category}
            </Badge>
          )}
          
          {item.publish_date && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {format(new Date(item.publish_date), 'MMM d, yyyy')}
            </Badge>
          )}
        </div>
      </div>
      
      {item.image_url && (
        <div className="relative w-full h-[300px] md:h-[400px] mb-8 rounded-lg overflow-hidden">
          <img 
            src={item.image_url} 
            alt={item.title} 
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      )}
      
      <div className="prose max-w-none dark:prose-invert">
        <RichTextViewer content={item.content} />
      </div>
      
      {item.source && (
        <div className="mt-8 pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Source: {item.source}
          </p>
        </div>
      )}
    </div>
  );
}
